import { Alert, Box, Button, Stack, Typography } from '@montugroup/design-system';
import PrintIcon from '@mui/icons-material/Print';
import type { Theme } from '@mui/material';
import { GlobalStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { StocktakeError } from '@/components/inventory/stocktake/StocktakeError';
import { StocktakeSkeleton } from '@/components/inventory/stocktake/StocktakeSkeleton';
import { compareByName } from '@/components/inventory/stocktake/stocktakeSort';
import type { StocktakeItem } from '@/hooks/stocktake/useStocktake';
import { useStocktake } from '@/hooks/stocktake/useStocktake';
import { DATE_DISPLAY_FORMAT } from '@/utils/helpers';

type PrintableColumn = {
  header: string;
  align: 'left' | 'right';
  width: string;
  cell: (item: StocktakeItem) => ReactNode;
};

const COLUMNS: readonly PrintableColumn[] = [
  { header: 'Product Name', align: 'left', width: '37%', cell: (item) => item.name },
  { header: 'SKU', align: 'left', width: '20%', cell: (item) => item.sku },
  { header: 'Expected Stock', align: 'right', width: '7%', cell: (item) => item.expectedStock },
  { header: 'Counted Stock', align: 'right', width: '7%', cell: (item) => item.countedStock },
  { header: 'Discrepancy', align: 'right', width: '9%', cell: (item) => formatDiscrepancy(item.discrepancy) },
  { header: 'Reason', align: 'left', width: '20%', cell: (item) => item.reason },
];

const BLANK_ROW_COUNT = 5;

const PRINT_FONT_SIZE = '9pt';

const PAGE_STYLES = (theme: Theme) => ({
  '@page': {
    size: 'A4 landscape',
    margin: '10mm',
    // Firefox and Safari ignore page margin boxes, so they print without page numbers
    '@bottom-right': { content: '"Page " counter(page) " of " counter(pages)' },
  },
  '@media print': {
    body: { backgroundColor: theme.palette.common.white },
    '*': { color: theme.palette.common.black, boxShadow: 'none' },
  },
});

// Leaves room to handwrite a count, and gives the blank rows the same height as product rows
const ROW_HEIGHT = '10mm';

// CssBaseline sits outside montuTheme, so the body never follows its dark scheme
const CONTAINER_SX = {
  minHeight: '100vh',
  backgroundColor: 'background.default',
  color: 'text.primary',
  '@media print': { backgroundColor: 'common.white' },
} as const;

const TABLE_SX = {
  tableLayout: 'fixed',
  '& tr': { breakInside: 'avoid' },
  '& th, & td': { border: 1, borderColor: 'text.primary' },
  '@media print': {
    '& th, & td': { fontSize: PRINT_FONT_SIZE, paddingX: 1, paddingY: 0.5, borderColor: 'common.black' },
  },
} as const;

const TABLE_BODY_SX = {
  '& tr': { height: ROW_HEIGHT },
  '& tr:nth-of-type(odd)': {
    backgroundColor: 'action.hover',
    '@media print': {
      backgroundColor: 'grey.200',
      // Browsers drop background colours when printing unless told to keep them
      printColorAdjust: 'exact',
      WebkitPrintColorAdjust: 'exact',
    },
  },
} as const;

const formatDiscrepancy = (discrepancy: number | undefined): string | undefined => {
  if (discrepancy === undefined) {
    return undefined;
  }
  return discrepancy > 0 ? `+${discrepancy}` : String(discrepancy);
};

const StocktakeRow = ({ item }: { item: StocktakeItem }) => {
  return (
    <TableRow>
      {COLUMNS.map((column) => (
        <TableCell key={column.header} align={column.align}>
          {column.cell(item)}
        </TableCell>
      ))}
    </TableRow>
  );
};

const BlankRow = () => {
  return (
    <TableRow data-testid="blank-row">
      {COLUMNS.map((column) => (
        <TableCell key={column.header} />
      ))}
    </TableRow>
  );
};

const PrintableStocktakeTable = ({ stocktakeItems }: { stocktakeItems: readonly StocktakeItem[] }) => {
  const [stocktakeDate] = useState(() => DateTime.now());
  const sortedItems = [...stocktakeItems].sort(compareByName);
  const productCount = sortedItems.length;

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4} paddingBottom={4}>
        <Typography variant="body1">
          Stocktake date: {stocktakeDate.toFormat(DATE_DISPLAY_FORMAT)} · {productCount}{' '}
          {productCount === 1 ? 'product' : 'products'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ displayPrint: 'none' }}
        >
          Print
        </Button>
      </Stack>
      <Table size="small" sx={TABLE_SX}>
        <TableHead sx={{ '& th': { fontWeight: 'fontWeightBold' } }}>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column.header} align={column.align} sx={{ width: column.width }}>
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={TABLE_BODY_SX}>
          {sortedItems.map((item) => (
            <StocktakeRow key={item.productId} item={item} />
          ))}
          {Array.from({ length: BLANK_ROW_COUNT }).map((_, i) => (
            <BlankRow key={i} />
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export function PrintableStocktake() {
  const { status, stocktakeItems, retry } = useStocktake();

  return (
    <Box padding={4} sx={CONTAINER_SX}>
      <GlobalStyles styles={PAGE_STYLES} />
      {status === 'loading' && <StocktakeSkeleton />}
      {status === 'error' && <StocktakeError onRetry={retry} />}
      {status === 'notStarted' && (
        <Alert severity="warning">No stocktake is in progress. Start one from the Stocktake tab.</Alert>
      )}
      {status === 'inProgress' && <PrintableStocktakeTable stocktakeItems={stocktakeItems} />}
    </Box>
  );
}
